import {
  ADVICE_TEXT_EN,
  ADVICE_TEXT_JA,
  ADVICE_LINK_EN,
  ADVICE_LINK_JA,
  LINK_PLACEHOLDER,
  AdviceLinkDefinition,
  AdviceLinkDefinitionSet,
} from '@/constants/advice';
import { IGetNursingHomeAdvice } from '@/types/api/advice/getNursingHomeAdvice';

interface AdvicePathParams {
  [key: string]: string | number;
}

export type AdviceTextPart =
  | {
      type: 'text';
      value: string;
    }
  | {
      type: 'link';
      isExternal: boolean;
      label: string;
      to: string;
    };

interface ParseAdviceTextParams {
  text: string;
  links?: AdviceLinkDefinitionSet;
  pathParams?: AdvicePathParams;
}

interface BuildAdviceTextPartsParams {
  advice: IGetNursingHomeAdvice;
  isJa: boolean;
  pathParams?: AdvicePathParams;
}

interface GetAdviceLinksParams {
  isJa: boolean;
  adviceType: string;
}

const normalizeLinks = (links?: AdviceLinkDefinitionSet): AdviceLinkDefinition[] => {
  if (!links) return [];
  return Array.isArray(links) ? links : [links];
};

const resolvePathParams = (path: string, params: AdvicePathParams): string => {
  return Object.entries(params).reduce((resolvedPath, [key, value]) => {
    return resolvedPath.replace(`:${key}`, String(value));
  }, path);
};

const isExternalLink = (to: string): boolean => {
  // "https://...", "mailto:...", "tel:..." などの絶対URL/スキーマ付きURLを外部リンク扱いとして判定
  return /^[a-z][a-z\d+\-.]*:/i.test(to) || to.startsWith('//');
};

export const getAdviceLinks = ({ isJa, adviceType }: GetAdviceLinksParams): AdviceLinkDefinitionSet | undefined => {
  const links = isJa ? ADVICE_LINK_JA : ADVICE_LINK_EN;
  return links[adviceType];
};

export const parseAdviceTextWithLinks = ({ text, links, pathParams = {} }: ParseAdviceTextParams): AdviceTextPart[] => {
  if (!text.includes(LINK_PLACEHOLDER)) {
    return [{ type: 'text', value: text }];
  }

  const normalizedLinks = normalizeLinks(links);
  const splitText = text.split(LINK_PLACEHOLDER);
  const parts: AdviceTextPart[] = [];

  splitText.forEach((segment, index) => {
    if (segment) {
      parts.push({ type: 'text', value: segment });
    }

    if (index === splitText.length - 1) {
      return;
    }

    const currentLink = normalizedLinks[index];
    if (!currentLink) {
      parts.push({ type: 'text', value: LINK_PLACEHOLDER });
      return;
    }

    const resolvedTo = resolvePathParams(currentLink.to, pathParams);
    parts.push({
      type: 'link',
      isExternal: isExternalLink(resolvedTo),
      label: currentLink.label,
      to: resolvedTo,
    });
  });

  return parts;
};

export const buildAdviceTextParts = ({ advice, isJa, pathParams = {} }: BuildAdviceTextPartsParams): AdviceTextPart[] => {
  const textMap = isJa ? ADVICE_TEXT_JA : ADVICE_TEXT_EN;
  const adviceText = advice.isAutomatic
    ? textMap[advice.adviceType] ?? textMap.default
    : advice.freeAdvice && advice.freeAdvice.length > 0
      ? advice.freeAdvice
      : textMap.default;

  if (!advice.isAutomatic) {
    return [{ type: 'text', value: adviceText }];
  }

  const adviceLinks = getAdviceLinks({ isJa, adviceType: advice.adviceType });
  return parseAdviceTextWithLinks({
    text: adviceText,
    links: adviceLinks,
    pathParams,
  });
};
